import Loading from "@/pages/loading";
import { APP_ROUTES, useAppTranslation, useLastUrlWasPage } from "@/utils";
import { bnplConsumerApis } from "@payping/api";
import { CuToman, FiArrowRight, PhRStoresront } from "@payping/icons";
import { dateFormats, moment } from "@payping/utils/dateUtils";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Collapse,
  Divider,
  Flex,
  Progress,
  Tabs,
  Tag,
  Typography,
  notification,
  useMessagedAsync,
  useMessagedAsyncFn,
} from "@payping/web-components";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { ConsumerOrderBriefStatus, InstallmentStatus } from "@payping/api/app/api/bnplConsumerTypes";
import type { InstallmentResponse } from "@payping/api/app/api/bnplConsumerTypes";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import HowToWriteChequeDrawer from "@/Components/how-to-write-cheque-drawer";
import OrderDetailsInstallmentCard from "./InstallmentCard";
import OrderDetailsPagePaymentBeforeMaturity from "./PaymentBeforeMaturity";
import OrderDetailsPayInstallment from "./PayInstallment";

enum InstallmentStatusEnum {
  unpaid = "unpaid",
  paid = "paid",
}
const OrderDetailsPage = () => {
  const { t } = useAppTranslation();

  const params = useParams();
  const code = params.code ?? "";
  const { navigateToLast } = useLastUrlWasPage();
  const navigate = useNavigate();
  const [installmentsStatus, setInstallmentsStatus] = useState<InstallmentStatusEnum>(InstallmentStatusEnum.unpaid);
  const [payInstallmentModal, setPayInstallmentModal] = useState<{ data?: InstallmentResponse; show: boolean }>({
    data: undefined,
    show: false,
  });
  const [paymentBeforeMaturityModal, setPaymentBeforeMaturityModal] = useState(false);
  const [showHowToWriteCheque, setShowHowToWriteCheque] = useState(false);
  const [notif, contextHolder] = notification.useNotification({
    bottom: 0,

    duration: 4,
    placement: "bottomRight",
  });

  const [isSend, setIsSend] = useState(false);

  const { loading, error, value } = useMessagedAsync(() => bnplConsumerApis.dashboardOrderDetail(code), [code], {
    success: false,
    error: true,
  });

  const installmentsState = useMessagedAsync(() => bnplConsumerApis.installmentsList(code), [code], {
    success: false,
    error: true,
  });

  const [claimDeliveryChequeState, claimDeliveryCheque] = useMessagedAsyncFn(
    async () => {
      try {
        const res = await bnplConsumerApis.claimChequeDelivery({
          trackingCode: code,
        });
        notif.success({ title: t("home.order.sendCheque.form.successMessage") });
        return res;
      } catch (e: any) {
        if (e.status === 400) {
          try {
            const parsedError = await e.json();
            throw parsedError;
          } catch (jsonError: any) {
            if (jsonError.status === 400) {
              throw jsonError;
            }
            throw e;
          }
        }
        throw e;
      }
    },
    [code, notif, t],
    { loading: false },
    { loading: false },
  );

  const installmentsList = installmentsState.value?.installments || [];
  const paidInstallmentsList = installmentsList.filter((i) => i.isPaid);
  const unPaidInstallmentsList = installmentsList.filter((i) => !i.isPaid);

  const haveSalaryWaitingInstallment = !!installmentsState.value?.installments.find(
    (i) => i.installmentStatus === InstallmentStatus.SalaryDeductionWaiting,
  );

  const isPaidOff = value?.status === ConsumerOrderBriefStatus.PaidOff;

  const orderInCancelFLow =
    value?.status === ConsumerOrderBriefStatus.Canceled || value?.status === ConsumerOrderBriefStatus.SemiCanceled;

  useEffect(() => {
    if (!code) {
      navigate(APP_ROUTES.home.purchaseHistory.url);
    }
  }, [code, navigate]);

  if (loading || error || installmentsState.error) {
    return <Loading />;
  }

  const handleOpenPayInstallmentModal = (data: InstallmentResponse) => {
    setPayInstallmentModal({ data, show: true });
  };
  const handleClosePayInstallmentModal = () => {
    setPayInstallmentModal({ data: undefined, show: false });
  };

  const handleOpenPaymentBeforeMaturityModal = () => {
    setPaymentBeforeMaturityModal(true);
  };
  const handleClosePaymentBeforeMaturityModal = () => {
    setPaymentBeforeMaturityModal(false);
  };

  const handleCloseDrawer = () => {
    navigateToLast();
  };

  const haveAnyWaitingSalary = !!installmentsList.find(
    (i) => i.installmentStatus === InstallmentStatus.SalaryDeductionWaiting,
  );

  const cancelDate = () => {
    if (!value?.canceledByMerchantDate) return "";
    try {
      return moment(value?.canceledByMerchantDate ?? new Date()).format(dateFormats.ui.jalaliDate);
    } catch {
      return "";
    }
  };

  return (
    <>
      {contextHolder}
      <Flex vertical className="tw:relative tw:h-full tw:w-full" gap={"32px"}>
        <Flex vertical>
          <Flex align="center" gap={"small"} className="tw:px-4! tw:py-3!">
            <Button icon={<FiArrowRight width={14} height={14} />} type="text" onClick={handleCloseDrawer} />
            <Typography.Text>{t("common.details")}</Typography.Text>
          </Flex>
          <Divider className="tw:m-0! tw:w-full tw:p-0!" />
        </Flex>

        <Flex vertical className="tw:w-full tw:flex-1 tw:overflow-auto tw:px-4!" gap={"large"}>
          {value?.status === ConsumerOrderBriefStatus.WaitingForChequeDelivery && !haveAnyWaitingSalary ? (
            <Alert type="warning" showIcon title={t("home.order.confirmChequeAlert")} />
          ) : null}

          {value?.status === ConsumerOrderBriefStatus.Canceled ? (
            <Alert type="error" showIcon title={t("home.order.canceledChequeAlert")} />
          ) : null}

          <Flex vertical gap={"middle"} className="tw:w-full">
            <Flex justify="space-between" align="center">
              <Flex align="center" gap={"small"}>
                <Card size="small" classNames={{ body: "tw:p-2! tw:flex tw:items-center" }}>
                  <PhRStoresront className="tw:p-0! tw:text-color-text-tertiary" width={20} />
                </Card>
                <Typography.Text>{value?.merchantName ?? ""}</Typography.Text>
              </Flex>
              {haveAnyWaitingSalary ? (
                <Tag> {t("home.order.status.salaryDeductionWaiting")}</Tag>
              ) : (
                <>
                  {value?.status === ConsumerOrderBriefStatus.Init ? <Tag> {t("home.order.status.init")}</Tag> : null}
                  {value?.status === ConsumerOrderBriefStatus.WaitingForChequeDelivery ? (
                    <Tag color="warning"> {t("home.order.status.waitingForChequeDelivery")}</Tag>
                  ) : null}
                  {value?.status === ConsumerOrderBriefStatus.PrePaid ? (
                    <Tag> {t("home.order.status.prePaid")}</Tag>
                  ) : null}
                  {value?.status === ConsumerOrderBriefStatus.SemiCanceled ? (
                    <Tag> {t("home.order.status.semiCanceled")}</Tag>
                  ) : null}
                  {value?.status === ConsumerOrderBriefStatus.Canceled ? (
                    <Tag> {t("home.order.status.canceled")}</Tag>
                  ) : null}

                  {value?.status === ConsumerOrderBriefStatus.PaidOff ? (
                    <Tag color="success"> {t("home.order.status.paidOff")}</Tag>
                  ) : null}
                  {value?.status === ConsumerOrderBriefStatus.Unknown ? (
                    <Tag> {t("home.order.status.unknown")}</Tag>
                  ) : null}
                </>
              )}
            </Flex>
            <Card size="small">
              <Flex vertical gap={"middle"} className="tw:w-full">
                <Flex className="tw:w-full tw:pt-4!" vertical align="center" justify="center" gap={"small"}>
                  <Flex align="center" gap={"2px"}>
                    <Typography.Title level={5} className="tw:m-0!">
                      {formatLocalizedNumber({ value: value?.remainedInstallmentAmount ?? 0 })}
                    </Typography.Title>
                    <CuToman width={24} height={24} />
                  </Flex>
                  <Typography.Text type="secondary">{t("common.remainedAmount")}</Typography.Text>
                  <Button
                    type="link"
                    disabled={
                      installmentsState.loading ||
                      installmentsState.error ||
                      haveSalaryWaitingInstallment ||
                      orderInCancelFLow ||
                      isPaidOff ||
                      value?.status === ConsumerOrderBriefStatus.WaitingForChequeDelivery
                    }
                    onClick={
                      installmentsState.loading ||
                      installmentsState.error ||
                      haveSalaryWaitingInstallment ||
                      orderInCancelFLow ||
                      isPaidOff ||
                      value?.status === ConsumerOrderBriefStatus.WaitingForChequeDelivery
                        ? undefined
                        : handleOpenPaymentBeforeMaturityModal
                    }
                  >
                    {t("common.paymentBeforeMaturity")}
                  </Button>
                </Flex>
                <Flex vertical gap={"small"} className="tw:w-full">
                  <Progress
                    percent={((value?.paidInstallmentCount ?? 1) * 100) / (value?.totalInstallmentCount ?? 1)}
                    format={() => (
                      <>{`${value?.paidInstallmentCount ?? 0} ${t("common.from")} ${value?.totalInstallmentCount ?? 0}`}</>
                    )}
                    size="default"
                  />
                  <Flex className="tw:w-full" justify="space-between" align="center">
                    <Flex vertical gap={"2px"} justify="center" align="start">
                      <Flex align="center">
                        <Typography.Text>
                          {formatLocalizedNumber({ value: value?.paidInstallmentAmount ?? 0 })}
                        </Typography.Text>
                        <CuToman width={24} height={24} />
                      </Flex>

                      <Typography.Text type="secondary">{t("common.paid")}</Typography.Text>
                    </Flex>
                    <Flex vertical gap={"2px"} justify="center" align="end">
                      <Flex align="center">
                        <Typography.Text>
                          {formatLocalizedNumber({ value: value?.totalPaymentAmount ?? 0 })}
                        </Typography.Text>
                        <CuToman width={24} height={24} />
                      </Flex>
                      <Typography.Text type="secondary">{t("common.totalRefund")}</Typography.Text>
                    </Flex>
                  </Flex>
                </Flex>
              </Flex>
            </Card>
            <Card size="small">
              <Flex vertical className="tw:w-full" gap={"middle"}>
                <Flex justify="space-between" align="center" className="tw:w-full">
                  <Typography.Text type="secondary">{t("common.monthlyInstallmentAmount")}</Typography.Text>
                  <Flex align="center">
                    <Typography.Text>
                      {formatLocalizedNumber({ value: value?.monthlyInstallmentTotalAmount ?? 0 })}
                    </Typography.Text>
                    <CuToman width={24} height={24} />
                  </Flex>
                </Flex>
                <Flex justify="space-between" align="center" className="tw:w-full">
                  <Typography.Text type="secondary">{t("common.orderCreateDate")}</Typography.Text>
                  <Typography.Text>
                    {moment(value?.orderCreatedDate ?? new Date()).format(dateFormats.ui.jalaliDate)}
                  </Typography.Text>
                </Flex>
                {orderInCancelFLow ? (
                  <>
                    {value?.canceledByMerchantDate ? (
                      <Flex justify="space-between" align="center" className="tw:w-full">
                        <Typography.Text type="secondary">{t("common.orderCancelDate")}</Typography.Text>
                        <Typography.Text type="secondary" className="tw:text-xs!">
                          {cancelDate()}
                        </Typography.Text>
                      </Flex>
                    ) : null}

                    <Flex justify="space-between" align="center" className="tw:w-full">
                      <Typography.Text type="secondary">{t("home.order.details.refund")}</Typography.Text>

                      <Flex align="center">
                        <Typography.Title level={5} className="tw:m-0!">
                          {formatLocalizedNumber({ value: value?.canceledByMerchantAmount ?? 0 })}
                        </Typography.Title>
                        <CuToman width={24} height={24} />
                      </Flex>
                    </Flex>
                  </>
                ) : null}
              </Flex>
            </Card>
          </Flex>
          <Flex vertical>
            <Typography.Title level={5} className="tw:m-0! tw:mb-1!">
              {t("common.installments")}
            </Typography.Title>
            <Tabs
              className="tw:m-0!"
              activeKey={installmentsStatus}
              items={[
                {
                  key: InstallmentStatusEnum.unpaid,
                  label: t("common.unPaid"),
                },
                {
                  key: InstallmentStatusEnum.paid.toString(),
                  label: t("common.paid"),
                },
              ]}
              onChange={(key) => {
                setInstallmentsStatus(key as InstallmentStatusEnum);
              }}
            />
            <Flex className="tw:mt-8!" vertical gap={"small"}>
              {installmentsStatus === InstallmentStatusEnum.paid ? (
                <>
                  {paidInstallmentsList.map((item, index) => (
                    <OrderDetailsInstallmentCard
                      orderStatus={value?.status}
                      installmentNumber={item?.number ?? 0}
                      amount={item.installmentFinalAmount ?? 0}
                      key={item.installmentCode || index}
                      dueDate={moment(item.dueDate) ?? moment(new Date())}
                      handlePay={() => {
                        handleOpenPayInstallmentModal(item);
                      }}
                      penaltyAmount={item.delayPenaltyAmount ?? 0}
                      isPayAble={item.isPayable}
                      installmentStatus={item.installmentStatus}
                    />
                  ))}
                </>
              ) : null}
              {installmentsStatus === InstallmentStatusEnum.unpaid ? (
                <>
                  {unPaidInstallmentsList.map((item, index) => (
                    <OrderDetailsInstallmentCard
                      orderStatus={value?.status}
                      installmentNumber={item?.number ?? 0}
                      amount={item.installmentFinalAmount ?? 0}
                      key={item.installmentCode || index}
                      dueDate={moment(item.dueDate) ?? moment(new Date())}
                      handlePay={() => {
                        handleOpenPayInstallmentModal(item);
                      }}
                      penaltyAmount={item.delayPenaltyAmount ?? 0}
                      isPayAble={item.isPayable}
                      installmentStatus={item.installmentStatus}
                    />
                  ))}
                </>
              ) : null}
            </Flex>
          </Flex>
          {value?.chequeInfo &&
          value?.chequeInfo?.hasClaimedChequeDelivery === false &&
          value?.status !== ConsumerOrderBriefStatus.Canceled ? (
            <Flex vertical className="tw:mt-10! tw:w-full" gap={"large"}>
              <Flex vertical gap="middle">
                <Flex align="center" justify="space-between" className="tw:w-full">
                  <Typography.Text>
                    <strong>{t("home.order.sendCheque.title")}</strong>
                  </Typography.Text>
                  <Button
                    type="link"
                    className="tw:p-0!"
                    onClick={() => {
                      setShowHowToWriteCheque(true);
                    }}
                  >
                    {t("howToWriteCheckDrawer.title")}
                  </Button>
                  <HowToWriteChequeDrawer
                    open={showHowToWriteCheque}
                    onClose={() => {
                      setShowHowToWriteCheque(false);
                    }}
                    data={{
                      amountRial: value?.chequeInfo?.amountRial,
                      duedate: value?.chequeInfo.duedate,
                      recipientIdentifier: value?.chequeInfo.recipientIdentifier,
                      recipientName: value?.chequeInfo.recipientName,
                      isLegal: value.chequeInfo.recipientIsLegal ?? false,
                    }}
                  />
                </Flex>
                <Typography.Text>{t("home.order.sendCheque.description")}</Typography.Text>
              </Flex>
              <Collapse
                expandIconPlacement="end"
                defaultActiveKey={"1"}
                items={[
                  {
                    key: "1",
                    label: <span className="tw:font-semibold">{t("home.order.sendCheque.form.title")}</span>,
                    children: (
                      <Flex vertical className="tw:w-full" gap={"small"}>
                        <Flex vertical className="tw:w-full" gap={"4px"}>
                          <Typography.Text type="secondary">
                            {t("home.order.sendCheque.form.agentName")}
                          </Typography.Text>
                          <Typography.Text>{value?.chequeInfo?.agentName ?? ""}</Typography.Text>
                        </Flex>
                        <Divider className="tw:m-0! tw:w-full tw:p-0!" />
                        <Flex vertical className="tw:w-full" gap={"4px"}>
                          <Typography.Text type="secondary">
                            {t("home.order.sendCheque.form.agentPhoneNumber")}
                          </Typography.Text>
                          <Typography.Text>{value?.chequeInfo?.agentMobileNumber ?? ""}</Typography.Text>
                        </Flex>
                        <Divider className="tw:m-0! tw:w-full tw:p-0!" />
                        <Flex vertical className="tw:w-full" gap={"4px"}>
                          <Typography.Text type="secondary">
                            {t("home.order.sendCheque.form.agentPostalCode")}
                          </Typography.Text>
                          <Typography.Text>{value?.chequeInfo?.postalCode ?? ""}</Typography.Text>
                        </Flex>
                        <Divider className="tw:m-0! tw:w-full tw:p-0!" />
                        <Flex vertical className="tw:w-full" gap={"4px"}>
                          <Typography.Text type="secondary">
                            {t("home.order.sendCheque.form.agentAddress")}
                          </Typography.Text>
                          <Typography.Text>{value?.chequeInfo?.address ?? ""}</Typography.Text>
                        </Flex>
                      </Flex>
                    ),
                  },
                ]}
              />

              <Checkbox
                checked={isSend}
                onChange={() => {
                  setIsSend((pre) => !pre);
                }}
                value={isSend}
              >
                {t("home.order.sendCheque.form.sendedCheque")}
              </Checkbox>

              <Flex vertical>
                <Divider className="tw:m-0!" />
                <Flex vertical className="tw:px-5! tw:py-4!">
                  <Button
                    color="primary"
                    variant="solid"
                    loading={claimDeliveryChequeState.loading}
                    disabled={!isSend || !!claimDeliveryChequeState.value}
                    onClick={() => {
                      claimDeliveryCheque();
                    }}
                  >
                    {t("common.confirm")}
                  </Button>
                </Flex>
              </Flex>
            </Flex>
          ) : null}
        </Flex>
      </Flex>

      {paymentBeforeMaturityModal && value ? (
        <OrderDetailsPagePaymentBeforeMaturity
          trackingCode={code}
          installmentCodes={installmentsList.filter((i) => !i.isPaid).map((i) => i.installmentCode ?? "")}
          data={value}
          onClose={handleClosePaymentBeforeMaturityModal}
          open={paymentBeforeMaturityModal}
        />
      ) : null}
      {payInstallmentModal.show && payInstallmentModal.data ? (
        <OrderDetailsPayInstallment
          data={payInstallmentModal.data}
          onClose={handleClosePayInstallmentModal}
          open={payInstallmentModal.show}
        />
      ) : null}
    </>
  );
};

export default OrderDetailsPage;
